import type { Subnet } from 'openstack/components/networks/model'
import {
  createAttachmentRow,
  isExplicitPort,
  isPrivateIpPort,
  NetworkAttachmentRow,
  NetworkAttachments,
  PORT_AUTOMATIC,
  PORT_PRIVATE_IP,
  SUBNET_AUTOMATIC,
  updateAttachmentRow,
} from '../CreateVirtualMachineModal.helpers'

/**
 * PCD-5420: the rules behind the network table in Create VM, kept separate from the UI.
 *
 * None of these functions touch React or change anything in place. Each one takes the current
 * `NetworkAttachments` (the map of network -> the list of NIC rows the VM will get on it) plus
 * whatever the user just did, and returns a brand-new map. The table cells in
 * `networkColumns.tsx` only read data and call these functions, so all the tricky logic lives
 * here and can be unit-tested without rendering a table. See `networkAttachmentRows.spec.md`.
 */

// The most NICs we allow on one VM. This is the practical limit the hypervisor can give a VM
// (libvirt PCI slots) and it counts every network together. Neutron's own port quota still
// applies on the server.
export const MAX_VM_NICS = 8

// A blank stand-in row, used when a network has no editable parent row yet (for example, its
// parent Ports cell is showing port chips). It uses the "Automatic" defaults and a blank rowId
// so it never gets saved.
const emptyRow = (): NetworkAttachmentRow => ({
  rowId: '',
  subnet: SUBNET_AUTOMATIC,
  port: PORT_AUTOMATIC,
  fixedIP: '',
})

// ────────────────────────── read-only helpers (never change state) ──────────────────────────

// FR-014: the row shown in the network's own (parent) cells. If the parent Ports cell is showing
// port chips, return a blank row so a "+ Add subnet" sub-row never gets pulled up into the parent
// slot (a port placed there wouldn't work).
export const getFirstAttachmentRow = (
  attachments: NetworkAttachments,
  networkId: string,
): NetworkAttachmentRow => {
  const rows = attachments[networkId] ?? []
  if (rows.some((r) => r.viaPortChips)) {
    return emptyRow()
  }
  // rows[0] is always the parent row: it can't be removed, and chip/sub-rows are added after it.
  // Returning rows[0] (instead of the first subnet-driven row) keeps an explicit-port parent
  // usable instead of being hidden behind a subnet-only "+ Add subnet" sub-row. Makes no
  // difference for the normal Automatic-parent case.
  return rows[0] ?? emptyRow()
}

// Ports already picked by the other rows on this network. A Neutron port can only back one NIC,
// so offering the same port twice would fail when the VM is created.
export const getSiblingPortIds = (
  attachments: NetworkAttachments,
  networkId: string,
  excludeRowId: string,
): string[] =>
  (attachments[networkId] ?? [])
    .filter(
      (row) =>
        row.rowId !== excludeRowId && row.chipOf !== excludeRowId && isExplicitPort(row.port),
    )
    .map((row) => row.port)

// FR-019: how many NICs the VM's OTHER networks already use. Subtract this from MAX_VM_NICS to
// see how many more NICs this network is still allowed to add.
export const nicsUsedByOtherNetworks = (
  attachments: NetworkAttachments,
  networkId: string,
): number =>
  Object.entries(attachments)
    .filter(([id]) => id !== networkId)
    .reduce((total, [, rows]) => total + rows.length, 0)

// ────────────────────────── internal helpers that build the row list ──────────────────────────

// The parent Ports dropdown works like a port picker: every port you select becomes its own chip
// row (its own NIC). "Automatic" and "Private IP" can't be mixed with ports, so picking either one
// collapses the chips back to a single row. "+ Add subnet" sub-rows are always left untouched.
const buildRowsFromPortSelection = (
  rows: NetworkAttachmentRow[],
  newValues: string[],
  prevValues: string[],
  maxRows: number,
): NetworkAttachmentRow[] => {
  const added = newValues.find((v) => !prevValues.includes(v))
  // Rows the chip picker does NOT control: anything created with "+ Add subnet". Selecting ports
  // never touches these sub-rows.
  const subRows = rows.filter((r) => !r.viaPortChips)

  if (added === PORT_AUTOMATIC || added === PORT_PRIVATE_IP) {
    // User picked Automatic or Private IP: clear the chips and set it on the parent row; sub-rows stay.
    const autos = subRows.filter((r) => !isExplicitPort(r.port))
    if (autos.length) {
      return subRows.map((r) =>
        r.rowId === autos[0].rowId ? { ...r, port: added, fixedIP: '' } : r,
      )
    }
    return [{ ...createAttachmentRow(), port: added }, ...subRows]
  }

  const selectedPorts = newValues.filter(isExplicitPort)
  // User removed every port from the parent: it goes back to an Automatic-subnet + Automatic-port
  // row. Any "+ Add subnet" sub-rows stay below it; none gets promoted up.
  if (selectedPorts.length === 0) {
    return [createAttachmentRow(), ...subRows]
  }

  // Once ports are picked, the parent's own placeholder row (Automatic/Private IP with no subnet)
  // is no longer needed; subnet-pinned sub-rows stay.
  const firstAuto = subRows.find((r) => !isExplicitPort(r.port))
  const keptSubRows = subRows.filter(
    (r) =>
      !(r.rowId === firstAuto?.rowId && (r.subnet === SUBNET_AUTOMATIC || isPrivateIpPort(r.port))),
  )
  const keptChips = rows.filter((r) => r.viaPortChips && selectedPorts.includes(r.port))
  const existing = new Set([
    ...keptChips.map((r) => r.port),
    ...keptSubRows.filter((r) => isExplicitPort(r.port)).map((r) => r.port),
  ])
  const roomLeft = Math.max(0, maxRows - keptSubRows.length - keptChips.length)
  const addedRows = selectedPorts
    .filter((portId) => !existing.has(portId))
    .slice(0, roomLeft)
    .map((portId) => ({
      // Picking a port never sets a subnet (rule 2): chip rows stay on Automatic subnet, and Nova
      // attaches the port by its ID.
      ...createAttachmentRow(),
      port: portId,
      viaPortChips: true,
    }))

  const next = [...keptSubRows, ...keptChips, ...addedRows]
  return next.length ? next : [createAttachmentRow()]
}

// Ports picked on a sub-row: the sub-row (the "anchor") keeps the first port; each extra port
// becomes its own chip row (chipOf = anchor's rowId) sharing the anchor's subnet — one NIC each.
// "Automatic" and "Private IP" can't be mixed in, so picking either collapses back to just the anchor.
const buildSubRowPortSelection = (
  rows: NetworkAttachmentRow[],
  anchor: NetworkAttachmentRow,
  newValues: string[],
  prevValues: string[],
  maxPorts: number,
): NetworkAttachmentRow[] => {
  const added = newValues.find((v) => !prevValues.includes(v))
  if (
    added === PORT_AUTOMATIC ||
    added === PORT_PRIVATE_IP ||
    !newValues.filter(isExplicitPort).length
  ) {
    const port = added === PORT_PRIVATE_IP ? PORT_PRIVATE_IP : PORT_AUTOMATIC
    return rows
      .filter((r) => r.chipOf !== anchor.rowId)
      .map((r) => (r.rowId === anchor.rowId ? { ...r, port, fixedIP: '' } : r))
  }
  const ports = newValues.filter(isExplicitPort).slice(0, Math.max(1, maxPorts))
  const [first, ...rest] = ports
  const result = rows.flatMap((r) => {
    if (r.rowId === anchor.rowId) {
      // Picking a port never changes the row's subnet (rule 2).
      return [{ ...r, port: first, fixedIP: '' }]
    }
    if (r.chipOf === anchor.rowId) {
      return rest.includes(r.port) ? [r] : []
    }
    return [r]
  })
  const existingChipPorts = new Set(
    rows.filter((r) => r.chipOf === anchor.rowId).map((r) => r.port),
  )
  const newChips = rest
    .filter((p) => !existingChipPorts.has(p))
    .map((p) => ({
      // Chips use the same subnet as the anchor row (Automatic unless the anchor picked one).
      ...createAttachmentRow(),
      subnet: anchor.subnet,
      port: p,
      chipOf: anchor.rowId,
    }))
  return [...result, ...newChips]
}

// ────────────────────────── user actions (each returns a new attachments map) ──────────────────────────

// Apply a field change to the parent row, creating it the first time it's edited. If every
// existing row is an explicit-port chip, start a fresh automatic row instead.
export const applyFirstRowChange = (
  attachments: NetworkAttachments,
  networkId: string,
  changes: Partial<Omit<NetworkAttachmentRow, 'rowId'>>,
): NetworkAttachments => {
  const existing = attachments[networkId] ?? []
  const target = existing.find((r) => !isExplicitPort(r.port))
  if (target) {
    return updateAttachmentRow(attachments, networkId, target.rowId, changes)
  }
  return { ...attachments, [networkId]: [...existing, { ...createAttachmentRow(), ...changes }] }
}

// FR-015: changing the parent's subnet only resets the PARENT SLOT — the rows the parent cells
// control (the chip rows in chip mode, otherwise the parent row plus any chips hanging off it) —
// down to one fresh automatic row on the chosen subnet. Separate "+ Add subnet" sub-rows are left
// alone, since the user added those on purpose.
export const applyParentSubnetChange = (
  attachments: NetworkAttachments,
  networkId: string,
  subnet: Subnet | '',
): NetworkAttachments => {
  const rows = attachments[networkId] ?? []
  const first = getFirstAttachmentRow(attachments, networkId)
  const hasChips = rows.some((r) => r.viaPortChips)
  const parentSlotIds = new Set(
    hasChips
      ? rows.filter((r) => r.viaPortChips).map((r) => r.rowId)
      : rows.filter((r) => r.rowId === first.rowId || r.chipOf === first.rowId).map((r) => r.rowId),
  )
  const subRows = rows.filter((r) => !parentSlotIds.has(r.rowId))
  return { ...attachments, [networkId]: [{ ...createAttachmentRow(), subnet }, ...subRows] }
}

// FR-014/019: handle the parent Ports multi-select, limiting new chip rows to the VM's remaining
// NIC budget.
export const applyParentPortSelection = (
  attachments: NetworkAttachments,
  networkId: string,
  newValues: string[],
  prevValues: string[],
): NetworkAttachments => {
  const rows = attachments[networkId] ?? []
  const maxRows = MAX_VM_NICS - nicsUsedByOtherNetworks(attachments, networkId)
  return {
    ...attachments,
    [networkId]: buildRowsFromPortSelection(rows, newValues, prevValues, maxRows),
  }
}

// FR-015: changing a sub-row's subnet resets that row's port/fixed IP and removes any chip rows it
// owned — the row goes back to one Automatic port on the chosen subnet.
export const applySubRowSubnetChange = (
  attachments: NetworkAttachments,
  networkId: string,
  anchorRowId: string,
  subnet: Subnet | '',
): NetworkAttachments => {
  const rows = attachments[networkId] ?? []
  return {
    ...attachments,
    [networkId]: rows
      .filter((r) => r.chipOf !== anchorRowId)
      .map((r) =>
        r.rowId === anchorRowId ? { ...r, subnet, port: PORT_AUTOMATIC, fixedIP: '' } : r,
      ),
  }
}

// FR-014/019: handle a sub-row's Ports multi-select, limiting that row's chip group to the VM's
// remaining NIC budget.
export const applySubRowPortSelection = (
  attachments: NetworkAttachments,
  networkId: string,
  anchor: NetworkAttachmentRow,
  newValues: string[],
  prevValues: string[],
): NetworkAttachments => {
  const rows = attachments[networkId] ?? []
  const chipCount = rows.filter((r) => r.chipOf === anchor.rowId).length
  const totalNics = Object.values(attachments).reduce((t, rs) => t + rs.length, 0)
  const maxGroupPorts = MAX_VM_NICS - (totalNics - 1 - chipCount)
  return {
    ...attachments,
    [networkId]: buildSubRowPortSelection(rows, anchor, newValues, prevValues, maxGroupPorts),
  }
}
